import { FormValues, SUCCESS_CHAMPION_PREFERENCES } from '../../types/ChampionApplicationFormTypes';
import { ENVIRONMENT, getEnvironment } from '../../utils/environment';
import Logger from 'services/sentry/sentry';

const DEV_GRAPHQL_PATH =
  '/gateway/api/graphql?serviceOverride=upp_service_api=https://upp-service-api.ap-southeast-2.dev.atl-paas.net/api/graphql';
const DEFAULT_GRAPHQL_PATH = '/gateway/api/graphql';

const getGraphqlPath = (): string => {
  const env = getEnvironment();
  return env === ENVIRONMENT.DEV ? DEV_GRAPHQL_PATH : DEFAULT_GRAPHQL_PATH;
};

const UNIFIED_PROFILE_QUERY = `
  query UNIFIED_PROFILE_QUERY($aaid: String!) {
    unified {
      unifiedProfile(aaid: $aaid) {
        ... on UnifiedProfile {
          internalId
        }
        ... on UnifiedQueryError {
          code
          identifier
          message
        }
      }
    }
  }
`;

export const USER_ACCOUNT_QUERY = `
  query USER_ACCOUNT_QUERY($aaid: String!) {
    unified {
      account(aaid: $aaid) {
        ... on UnifiedAccount {
          aaid
          atlassianOneUserId
          emailId
          id
          internalId
          isLinked
          isManaged
          isPrimary
          isProfileOwnerConsented
          khorosUserId
        }
        ... on UnifiedQueryError {
          code
          identifier
          message
        }
      }
    }
  }
`;

export const SUBMIT_CHAMPION_SIGNUP_MUTATION = `
  mutation SUBMIT_CHAMPION_SIGNUP_MUTATION($input: UnifiedChampionSignupInput) {
    champion {
      championSignup {
        submitChampionSignup(input: $input) {
          success
          message
        }
      }
    }
  }
`;

export const SET_CHAMPION_PAGE_CONSENT_MUTATION = `
  mutation SET_CHAMPION_PAGE_CONSENT_MUTATION($input: UnifiedChampionConsentInput!) {
    champion {
      championConsent {
        setChampionPageConsent(input: $input) {
          success
          message
        }
      }
    }
  }
`;

export type ChampionSignupResponseValues = {
  success: boolean;
  message?: string;
};

export type ChampionSignupMutationResponse = {
  data?: {
    champion?: {
      championSignup?: {
        submitChampionSignup?: ChampionSignupResponseValues;
      };
    };
  };
};

export type UnifiedChampionConsentInput = {
  aaid: string;
  consentProgramAgreement: boolean;
  consentSuccessChampionAgreement: boolean;
};

export type ChampionConsentMutationPayload = {
  success: boolean;
  message?: string | null;
};

export type ChampionConsentMutationResponse = {
  data?: {
    champion?: {
      championConsent?: {
        setChampionPageConsent?: ChampionConsentMutationPayload | null;
      };
    };
  };
};

export const setChampionPageConsent = async (
  input: UnifiedChampionConsentInput,
  baseUrl: string = '',
  Origin?: string,
): Promise<ChampionConsentMutationPayload | null> => {
  try {
    const rawResponse = await fetch(`${baseUrl}${getGraphqlPath()}`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...(Origin ? { Origin } : {}),
      },
      body: JSON.stringify({
        query: SET_CHAMPION_PAGE_CONSENT_MUTATION,
        operationName: 'SET_CHAMPION_PAGE_CONSENT_MUTATION',
        variables: { input },
      }),
    });
    const json: ChampionConsentMutationResponse = await rawResponse.json();
    return json?.data?.champion?.championConsent?.setChampionPageConsent ?? null;
  } catch (err) {
    const error =
      err instanceof Error
        ? err
        : new Error('[ChampionGraphQlOperations] setChampionPageConsent failed');
    Logger.error(error, {
      extra: {
        aaid: input.aaid,
        ...(err instanceof Error ? {} : { thrown: err }),
      },
    });
    return null;
  }
};

export const submitChampionSignup = async (
  input: FormValues,
  baseUrl: string = '',
  Origin?: string,
): Promise<ChampionSignupResponseValues | null> => {
  try {
    const isSuccessChampionTrack =
      input.championPreference !== null &&
      SUCCESS_CHAMPION_PREFERENCES.includes(input.championPreference);

    const { consentSuccessChampionAgreement: _omitSuccessChampionConsent, ...inputWithoutSuccessChampionConsent } =
      input;
    const signupInput = isSuccessChampionTrack ? input : inputWithoutSuccessChampionConsent;

    const rawResponse = await fetch(`${baseUrl}${getGraphqlPath()}`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...(Origin ? { Origin } : {}),
      },
      body: JSON.stringify({
        query: SUBMIT_CHAMPION_SIGNUP_MUTATION,
        operationName: 'SUBMIT_CHAMPION_SIGNUP_MUTATION',
        variables: { input: signupInput },
      }),
    });
    const json: ChampionSignupMutationResponse = await rawResponse.json();
    return json?.data?.champion?.championSignup?.submitChampionSignup ?? null;
  } catch (err) {
    const error =
      err instanceof Error ? err : new Error('[ChampionGraphQlOperations] submitChampionSignup failed');
    Logger.error(error, {
      extra: {
        aaid: input.aaid,
        ...(err instanceof Error ? {} : { thrown: err }),
      },
    });
    return null;
  }
};

export type UserAccountResponse = {
  data?: {
    unified?: {
      account?: {
        aaid?: string;
        atlassianOneUserId?: string | null;
        emailId?: string;
        id?: string;
        internalId?: string;
        isLinked?: boolean;
        isManaged?: boolean;
        isPrimary?: boolean;
        isProfileOwnerConsented?: boolean;
        khorosUserId?: number;
        code?: string;
        identifier?: string;
        message?: string;
      };
    };
  };
};

export const getUserAccountStatuses = async (
  aaid: string,
  baseUrl: string = '',
  Origin?: string,
): Promise<UserAccountResponse | null> => {
  try {
    const rawResponse = await fetch(`${baseUrl}${getGraphqlPath()}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(Origin ? { Origin } : {}),
      },
      body: JSON.stringify({
        query: USER_ACCOUNT_QUERY,
        operationName: 'USER_ACCOUNT_QUERY',
        variables: { aaid },
      }),
    });
    return await rawResponse.json();
  } catch (err) {
    const error =
      err instanceof Error ? err : new Error('[ChampionGraphQlOperations] getUserAccountStatuses failed');
    Logger.error(error, {
      extra: {
        aaid,
        ...(err instanceof Error ? {} : { thrown: err }),
      },
    });
    return null;
  }
};

export const getProfileInternalId = async (
  aaid: string,
  baseUrl: string = '',
  Origin?: string,
): Promise<string | null> => {
  try {
    const rawResponse = await fetch(`${baseUrl}${getGraphqlPath()}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(Origin ? { Origin } : {}),
      },
      body: JSON.stringify({
        query: UNIFIED_PROFILE_QUERY,
        operationName: 'UNIFIED_PROFILE_QUERY',
        variables: { aaid },
      }),
    });

    const json = await rawResponse.json();
    const profile = json?.data?.unified?.unifiedProfile;

    if (!profile || 'code' in profile || !profile.internalId) return null;

    return profile.internalId;
  } catch (err) {
    const error =
      err instanceof Error ? err : new Error('[ChampionGraphQlOperations] getProfileInternalId failed');
    Logger.error(error, {
      extra: {
        aaid,
        ...(err instanceof Error ? {} : { thrown: err }),
      },
    });
    return null;
  }
};
